import React, { useState, useEffect, useRef } from 'react';
import './GlobalCursor.css';
const GlobalCursor = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [svgPosition, setSvgPosition] = useState({ x: 0, y: 0 });
  const [velocity, setVelocity] = useState({ x: 0, y: 0 });
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });
  const [lastTime, setLastTime] = useState(Date.now());
  const [isSticky, setIsSticky] = useState(false);
  const [stickyTarget, setStickyTarget] = useState({ 
    x: 0, y: 0, width: 0, height: 0, borderRadius: 0 
  });
  const [scale, setScale] = useState({ x: 1, y: 1 });
  const [rotation, setRotation] = useState(0);
  const [isClicked, setIsClicked] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [circleSize, setCircleSize] = useState({ width: 60, height: 60, borderRadius: 30 });
  const [showCircle, setShowCircle] = useState(false);
  const [buttonHasBackground, setButtonHasBackground] = useState(false);
  const [stretchEffect, setStretchEffect] = useState({ x: 1, y: 1 });
  const [isPulling, setIsPulling] = useState(false);
  const [circleOffset, setCircleOffset] = useState({ x: 0, y: 0 });
  const [circleSquash, setCircleSquash] = useState({ x: 1, y: 1 });
  const [isInsideStickyArea, setIsInsideStickyArea] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  
  // Состояние для отладки
  const [debugInfo, setDebugInfo] = useState({
    show: false,
    x: 0,
    y: 0,
    circleWidth: 0,
    circleHeight: 0,
    borderRadius: 0,
    isInsideButton: false,
    buttonHasBackground: false,
    stretchEffect: { x: 1, y: 1 },
    offset: { x: 0, y: 0 },
    squash: { x: 1, y: 1 },
    elementType: '',
    elementWidth: 0,
    elementHeight: 0,
    elementBorderRadius: 0
  });
  
  const rafRef = useRef(null);
  const positionHistoryRef = useRef([]);
  const rotationHistoryRef = useRef([]);
  const stickyElementRef = useRef(null);
  const offsetHistoryRef = useRef([]);
  const stickStartTimeRef = useRef(0);
  const isDetachingRef = useRef(false);
  const lastStickyStateRef = useRef(false);
  const lastElementRef = useRef(null);
  const logoVelocityRef = useRef({ x: 0, y: 0 });
  const logoPositionRef = useRef({ x: 0, y: 0 });
  const isInsideButtonRef = useRef(false);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const logoAnimationRef = useRef({ x: 0, y: 0 });
  const hoveredElementRef = useRef(null);
  const lastCircleSizeRef = useRef({ width: 60, height: 60, borderRadius: 30 });
  
  const MAX_HISTORY = 5;
  const ROTATION_SMOOTH_HISTORY = 3;
  const OFFSET_SMOOTH_HISTORY = 3;
  const PADDING_NO_BG = 15; // Единый padding для кнопок без фона

  const lerp = (start, end, factor) => {
    return start + (end - start) * factor;
  };

  const distance = (x1, y1, x2, y2) => {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  };

  // Функция для проверки есть ли у элемента фон
  const hasBackground = (element) => {
    if (!element) return false;
    
    try {
      const style = window.getComputedStyle(element);
      const backgroundColor = style.backgroundColor;
      const backgroundImage = style.backgroundImage;
      
      const hasBgColor = backgroundColor && 
                        backgroundColor !== 'rgba(0, 0, 0, 0)' && 
                        backgroundColor !== 'transparent';
      
      const hasBgImage = backgroundImage && backgroundImage !== 'none';
      const hasBoxShadow = style.boxShadow && style.boxShadow !== 'none';
      
      return hasBgColor || hasBgImage || hasBoxShadow;
    } catch (e) {
      return false;
    }
  };

  // Функция для получения точных размеров элемента
  const getElementMetrics = (element) => {
    const rect = element.getBoundingClientRect();
    
    const hasBg = hasBackground(element);
    setButtonHasBackground(hasBg);
    
    // Определяем, маленькая ли круглая кнопка
    const isSmallButton = rect.width < 80 && rect.height < 80;
    const computedStyle = window.getComputedStyle(element);
    const borderRadius = parseFloat(computedStyle.borderRadius);
    const isRound = borderRadius >= rect.width * 0.4 || borderRadius >= rect.height * 0.4;
    const isSmallRoundButton = isSmallButton && isRound;
    
    // Определяем, является ли кнопка маленькой (до 100px)
    const isSmallElement = rect.width <= 100 && rect.height <= 100;
    
    if (hasBg) {
      // Для кнопок с фоном используем точные размеры кнопки
      const finalWidth = rect.width;
      const finalHeight = rect.height;
      
      // Для кнопок с фоном используем их собственный border-radius
      const finalBorderRadius = borderRadius;
      
      return {
        x: rect.left,
        y: rect.top,
        width: finalWidth,
        height: finalHeight,
        borderRadius: finalBorderRadius,
        center: {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        },
        originalRect: rect,
        hasBackground: true,
        isSmallRoundButton,
        isSmallElement,
        elementType: element.tagName.toLowerCase(),
        elementWidth: rect.width,
        elementHeight: rect.height,
        elementBorderRadius: borderRadius
      };
    } else {
      // Для кнопок без фона добавляем единый отступ PADDING_NO_BG
      const padding = PADDING_NO_BG;
      
      const finalWidth = rect.width + (padding * 2);
      const finalHeight = rect.height + (padding * 2);
      
      const minSide = Math.min(finalWidth, finalHeight);
      const finalBorderRadius = Math.min(30, minSide / 2);
      
      return {
        x: rect.left - padding,
        y: rect.top - padding,
        width: finalWidth,
        height: finalHeight,
        borderRadius: finalBorderRadius,
        center: {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        },
        originalRect: rect,
        hasBackground: false,
        isSmallRoundButton,
        isSmallElement,
        elementType: element.tagName.toLowerCase(),
        elementWidth: rect.width,
        elementHeight: rect.height,
        elementBorderRadius: borderRadius
      };
    }
  };

  // Проверка находится ли курсор внутри области прилипшей кнопки
  const isCursorInsideStickyArea = (cursorPos, metrics) => {
    if (!metrics) return false;
    
    // Для кнопок с фоном используем точные границы кнопки
    if (metrics.hasBackground) {
      return cursorPos.x >= metrics.originalRect.left && 
             cursorPos.x <= metrics.originalRect.right && 
             cursorPos.y >= metrics.originalRect.top && 
             cursorPos.y <= metrics.originalRect.bottom;
    } else {
      // Для кнопок без фона используем расширенную область с padding
      return cursorPos.x >= metrics.x && 
             cursorPos.x <= metrics.x + metrics.width && 
             cursorPos.y >= metrics.y && 
             cursorPos.y <= metrics.y + metrics.height;
    }
  };

  // Эффект растягивания - ТОЛЬКО для кнопок без фона и только когда курсор за пределами кнопки
  const calculateStretchEffect = (cursorPos, buttonCenter, elementRect, isSmallRoundButton, hasBackground, isInside) => {
    if (!isSticky || !elementRect || hasBackground || isInside) {
      // Для кнопок с фоном НЕТ растягивания
      // Также НЕТ растягивания когда курсор внутри кнопки
      setIsPulling(false);
      return { x: 1, y: 1 };
    }
    
    const dx = cursorPos.x - buttonCenter.x;
    const dy = cursorPos.y - buttonCenter.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    // Параметры только для кнопок без фона
    const maxDistForEffect = Math.max(elementRect.width, elementRect.height) * 1.5;
    const normalizedDist = Math.min(dist / maxDistForEffect, 1);
    
    const stretchIntensity = normalizedDist * 0.5;
    const angle = Math.atan2(dy, dx);
    
    const stretchX = 1 + Math.abs(Math.cos(angle)) * stretchIntensity;
    const stretchY = 1 + Math.abs(Math.sin(angle)) * stretchIntensity;
    
    // Порог для состояния "оттягивания"
    setIsPulling(dist > 20);
    
    return {
      x: Math.min(stretchX, 2.0),
      y: Math.min(stretchY, 2.0)
    };
  };

  // Расчет смещения круга в сторону курсора - ТОЛЬКО для кнопок без фона и только когда курсор за пределами кнопки
  const calculateCircleOffset = (cursorPos, buttonCenter, elementRect, isSmallRoundButton, hasBackground, isInside) => {
    if (!isSticky || !elementRect || hasBackground || isInside) {
      // Для кнопок с фоном НЕТ смещения
      // Также НЕТ смещения когда курсор внутри кнопки
      return { x: 0, y: 0 };
    }
    
    const dx = cursorPos.x - buttonCenter.x;
    const dy = cursorPos.y - buttonCenter.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    // Параметры только для кнопок без фона
    const maxOffsetDist = Math.max(elementRect.width, elementRect.height) * 1.8;
    const maxOffset = Math.min(elementRect.width, elementRect.height) * 0.4;
    
    if (dist < maxOffsetDist) {
      const offsetStrength = Math.min(dist / maxOffsetDist, 1);
      const smoothOffsetStrength = Math.pow(offsetStrength, 1.5);
      
      return {
        x: dx * smoothOffsetStrength * maxOffset / maxOffsetDist,
        y: dy * smoothOffsetStrength * maxOffset / maxOffsetDist
      };
    }
    
    // Даже если курсор далеко, все равно смещаем пузырь немного
    const maxAllowedOffset = maxOffset * 0.3;
    const clampedOffset = {
      x: Math.max(-maxAllowedOffset, Math.min(maxAllowedOffset, dx * 0.3)),
      y: Math.max(-maxAllowedOffset, Math.min(maxAllowedOffset, dy * 0.3))
    };
    
    return clampedOffset;
  };

  // Расчет сплющивания круга - ТОЛЬКО для кнопок без фона и только когда курсор за пределами кнопки
  const calculateCircleSquash = (cursorPos, buttonCenter, elementRect, offset, isSmallRoundButton, hasBackground, isInside) => {
    if (!isSticky || !elementRect || hasBackground || isInside) {
      // Для кнопок с фоном НЕТ сплющивания
      // Также НЕТ сплющивания когда курсор внутри кнопки
      return { x: 1, y: 1 };
    }
    
    const dx = cursorPos.x - (buttonCenter.x + offset.x);
    const dy = cursorPos.y - (buttonCenter.y + offset.y);
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    const maxSquashDist = Math.max(elementRect.width, elementRect.height) * 1.0;
    
    if (dist > 0) {
      const squashStrength = Math.min(dist / maxSquashDist, 0.2);
      const angle = Math.atan2(dy, dx);
      
      const squashX = 1 - Math.abs(Math.cos(angle)) * squashStrength;
      const squashY = 1 - Math.abs(Math.sin(angle)) * squashStrength;
      
      const minSquash = 0.7;
      
      return {
        x: Math.max(minSquash, squashX),
        y: Math.max(minSquash, squashY)
      };
    }
    
    return { x: 1, y: 1 };
  };

  // Функция для поиска интерактивного элемента под курсором
  const findInteractiveElementUnderCursor = (cursorPos) => {
    const interactiveSelectors = [
      'button',
      'a[href]',
      '[role="button"]',
      '.logo',
      '.logo-image',
      '[data-discover="true"]'
      // ИСКЛЮЧАЕМ поля формы: input, textarea, select
    ];
    
    // Используем document.elementsFromPoint для получения всех элементов под курсором
    const elements = document.elementsFromPoint(cursorPos.x, cursorPos.y);
    
    // Ищем первый интерактивный элемент
    for (const element of elements) {
      // Проверяем сам элемент
      const tagName = element.tagName.toLowerCase();
      const role = element.getAttribute('role');
      const className = element.className || '';
      
      // ИСКЛЮЧАЕМ поля формы
      const isFormField = tagName === 'input' || 
                         tagName === 'textarea' || 
                         tagName === 'select';
      
      if (isFormField) {
        // Для полей формы НЕ возвращаем элемент - не прилипаем к ним
        continue;
      }
      
      const isInteractive = (
        tagName === 'button' ||
        tagName === 'a' ||
        role === 'button' ||
        className.includes('logo') ||
        element.hasAttribute('data-discover')
      );
      
      if (isInteractive) {
        return element;
      }
      
      // Проверяем родителей на наличие интерактивных классов
      let parent = element.parentElement;
      while (parent && parent !== document.body) {
        const parentClassName = parent.className || '';
        const parentRole = parent.getAttribute('role');
        
        if (parentClassName.includes('logo') || parentRole === 'button') {
          return parent;
        }
        parent = parent.parentElement;
      }
    }
    
    return null;
  };

  // Функция для поиска ближайшего интерактивного элемента
  const findClosestInteractiveElement = (cursorPos) => {
    const interactiveSelectors = [
      'button',
      'a[href]',
      '[role="button"]',
      '.logo',
      '.logo-image',
      '[data-discover="true"]'
      // ИСКЛЮЧАЕМ поля формы: input, textarea, select
    ];
    
    const interactiveElements = document.querySelectorAll(interactiveSelectors.join(','));
    let closestElement = null;
    let closestDistance = Infinity;
    
    interactiveElements.forEach(element => {
      // Проверяем, не является ли элемент полем формы
      const tagName = element.tagName.toLowerCase();
      const isFormField = tagName === 'input' || 
                         tagName === 'textarea' || 
                         tagName === 'select';
      
      // ИСКЛЮЧАЕМ поля формы
      if (isFormField) {
        return;
      }
      
      const rect = element.getBoundingClientRect();
      const center = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
      
      const dist = distance(cursorPos.x, cursorPos.y, center.x, center.y);
      
      if (dist < closestDistance) {
        closestDistance = dist;
        closestElement = element;
      }
    });
    
    return { element: closestElement, distance: closestDistance };
  };

  // Функция для обновления размера круга
  const updateCircleSize = (metrics) => {
    // Сравниваем новые размеры с текущими
    const currentWidth = lastCircleSizeRef.current.width;
    const currentHeight = lastCircleSizeRef.current.height;
    const newWidth = metrics.width;
    const newHeight = metrics.height;
    
    // Обновляем только если размеры изменились более чем на 5%
    const widthDiff = Math.abs(newWidth - currentWidth) / currentWidth;
    const heightDiff = Math.abs(newHeight - currentHeight) / currentHeight;
    
    if (widthDiff > 0.05 || heightDiff > 0.05) {
      setCircleSize({
        width: metrics.width,
        height: metrics.height,
        borderRadius: metrics.borderRadius
      });
      
      // Сохраняем новые размеры в ref
      lastCircleSizeRef.current = {
        width: metrics.width,
        height: metrics.height,
        borderRadius: metrics.borderRadius
      };
    }
  };

  // Функция для проверки открыт ли попап
  const checkIfPopupIsOpen = () => {
    // Проверяем различные типы попапов
    const popupSelectors = [
      '.video-popup-overlay',
      '.popup-overlay',
      '.modal-overlay',
      '.modal-open',
      '.popup-open'
    ];
    
    // 1. Проверяем CSS классы на body
    const body = document.body;
    const bodyHasPopupClass = popupSelectors.some(selector => 
      body.classList.contains(selector.replace('.', ''))
    );
    
    // 2. Проверяем overflow hidden на body (часто при попапах)
    const bodyOverflowHidden = body.style.overflow === 'hidden';
    
    // 3. Проверяем наличие попапов в DOM
    let hasVisiblePopup = false;
    
    popupSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        if (element) {
          const styles = window.getComputedStyle(element);
          const isVisible = styles.display !== 'none' && 
                           styles.visibility !== 'hidden' && 
                           styles.opacity !== '0' &&
                           styles.pointerEvents !== 'none';
          
          if (isVisible) {
            hasVisiblePopup = true;
          }
        }
      });
    });
    
    // 4. Проверяем iframe (Vimeo/YouTube попапы)
    const iframes = document.querySelectorAll('iframe');
    const hasFullscreenIframe = Array.from(iframes).some(iframe => {
      try {
        // Проверяем, находится ли iframe в полноэкранном режиме
        const rect = iframe.getBoundingClientRect();
        const isLarge = rect.width > window.innerWidth * 0.8 && 
                       rect.height > window.innerHeight * 0.8;
        return isLarge;
      } catch (e) {
        return false;
      }
    });
    
    return bodyHasPopupClass || bodyOverflowHidden || hasVisiblePopup || hasFullscreenIframe;
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      const now = Date.now();
      const dt = Math.min(now - lastTime, 32);
      
      if (dt > 0) {
        const newVelocity = {
          x: (e.clientX - lastPosition.x) / dt * 16,
          y: (e.clientY - lastPosition.y) / dt * 16
        };
        
        setVelocity(prev => ({
          x: lerp(prev.x, newVelocity.x, 0.5),
          y: lerp(prev.y, newVelocity.y, 0.5)
        }));
        
        setLastTime(now);
        setLastPosition({ x: e.clientX, y: e.clientY });
      }
      
      // Обновляем текущую позицию мыши
      mousePositionRef.current = { x: e.clientX, y: e.clientY };
      
      positionHistoryRef.current.push({ x: e.clientX, y: e.clientY });
      if (positionHistoryRef.current.length > MAX_HISTORY) {
        positionHistoryRef.current.shift();
      }
      
      const avgPosition = positionHistoryRef.current.reduce(
        (acc, pos) => ({ x: acc.x + pos.x, y: acc.y + pos.y }),
        { x: 0, y: 0 }
      );
      const count = positionHistoryRef.current.length;
      
      setPosition({ 
        x: avgPosition.x / count, 
        y: avgPosition.y / count 
      });
    };

    const handleMouseDown = () => {
      setIsClicked(true);
      setTimeout(() => setIsClicked(false), 200);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [lastPosition, lastTime]);

  // Эффект для проверки открытия/закрытия попапов
  useEffect(() => {
    const checkPopupStatus = () => {
      const isOpen = checkIfPopupIsOpen();
      if (isOpen !== isPopupOpen) {
        setIsPopupOpen(isOpen);
        
        // Если попап открылся, сбрасываем все состояния курсора
        if (isOpen) {
          setIsSticky(false);
          setShowCircle(false);
          setIsInsideStickyArea(false);
          isInsideButtonRef.current = false;
          stickyElementRef.current = null;
          setCircleOffset({ x: 0, y: 0 });
          setCircleSquash({ x: 1, y: 1 });
          setStretchEffect({ x: 1, y: 1 });
          setIsPulling(false);
          offsetHistoryRef.current = [];
        }
      }
    };
    
    // Проверяем каждые 50мс для быстрого реагирования
    const interval = setInterval(checkPopupStatus, 50);
    
    // Также проверяем при изменении размера окна
    window.addEventListener('resize', checkPopupStatus);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', checkPopupStatus);
    };
  }, [isPopupOpen]);

  useEffect(() => {
    const checkHover = () => {
      // Если попап открыт, не проверяем ховер
      if (isPopupOpen) {
        setIsHovering(false);
        return;
      }
      
      const elementUnderCursor = findInteractiveElementUnderCursor(position);
      const isOverInteractive = !!elementUnderCursor;
      
      setIsHovering(isOverInteractive);
      
      // Сохраняем элемент под курсором для использования в анимации
      if (elementUnderCursor) {
        hoveredElementRef.current = elementUnderCursor;
      }
    };

    const interval = setInterval(checkHover, 40);
    return () => clearInterval(interval);
  }, [position, isPopupOpen]);

  useEffect(() => {
    const animate = () => {
      // ЕСЛИ ПОПАП ОТКРЫТ - ПРЕКРАЩАЕМ АНИМАЦИЮ КУРСОРА
      if (isPopupOpen) {
        // Продолжаем цикл анимации, но ничего не делаем
        rafRef.current = requestAnimationFrame(animate);
        return;
      }

      let targetX = position.x;
      let targetY = position.y;
      let smoothFactor = 0.1; // Единый smooth factor для всех состояний

      const speed = Math.sqrt(velocity.x ** 2 + velocity.y ** 2);
      const maxSpeed = 50;
      const speedRatio = Math.min(speed / maxSpeed, 2);
      
      // Логика вращения лого и его сплющивания от скорости
      if (!isSticky) {
        if (speed > 10) {
          const angle = Math.atan2(velocity.y, velocity.x);
          
          rotationHistoryRef.current.push(angle);
          if (rotationHistoryRef.current.length > ROTATION_SMOOTH_HISTORY) {
            rotationHistoryRef.current.shift();
          }
          
          let sinSum = 0;
          let cosSum = 0;
          
          rotationHistoryRef.current.forEach(a => {
            sinSum += Math.sin(a);
            cosSum += Math.cos(a);
          });
          
          const avgAngle = Math.atan2(sinSum / rotationHistoryRef.current.length, 
                                     cosSum / rotationHistoryRef.current.length);
          
          setRotation(prev => {
            const diff = avgAngle - prev;
            const normalizedDiff = Math.atan2(Math.sin(diff), Math.cos(diff));
            return prev + normalizedDiff * 0.3;
          });
          
          const stretchAmount = 1 + speedRatio * 0.5;
          const squeezeAmount = 1 - speedRatio * 0.2;
          
          setScale({
            x: stretchAmount,
            y: squeezeAmount
          });
        } else {
          setRotation(prev => {
            const diff = -prev;
            const normalizedDiff = Math.atan2(Math.sin(diff), Math.cos(diff));
            return prev + normalizedDiff * 0.08;
          });
          
          setScale({
            x: lerp(scale.x, 1, 0.15),
            y: lerp(scale.y, 1, 0.15)
          });
        }
      } else {
        setRotation(0);
        setScale({ x: 1, y: 1 });
      }

      // ВАЖНОЕ ИЗМЕНЕНИЕ: сначала проверяем элемент ПОД курсором
      const elementUnderCursor = findInteractiveElementUnderCursor(position);
      let closestElement = null;
      let closestDistance = Infinity;
      
      const STICKY_THRESHOLD_IN = 50;
      const STICKY_THRESHOLD_OUT_LARGE = 140;
      const STICKY_THRESHOLD_OUT_SMALL = 60;
      
      // Если есть элемент под курсором, используем его
      if (elementUnderCursor) {
        const rect = elementUnderCursor.getBoundingClientRect();
        const center = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
        
        const dist = distance(position.x, position.y, center.x, center.y);
        closestElement = elementUnderCursor;
        closestDistance = dist;
      } else {
        // Если под курсором нет интерактивного элемента, ищем ближайший
        const { element: nearestElement, distance: nearestDistance } = findClosestInteractiveElement(position);
        closestElement = nearestElement;
        closestDistance = nearestDistance;
      }
      
      // Проверяем, нужно ли прилипнуть
      if (closestElement && closestDistance < STICKY_THRESHOLD_IN && !isSticky) {
        const metrics = getElementMetrics(closestElement);
        const isInside = isCursorInsideStickyArea(position, metrics);
        setIsInsideStickyArea(isInside);
        isInsideButtonRef.current = isInside;
        
        setShowCircle(true);
        
        // Обновляем размер круга с помощью новой функции
        updateCircleSize(metrics);
        
        // Обновляем отладочную информацию
        setDebugInfo({
          show: true,
          x: metrics.center.x,
          y: metrics.center.y,
          circleWidth: metrics.width,
          circleHeight: metrics.height,
          borderRadius: metrics.borderRadius,
          isInsideButton: isInside,
          buttonHasBackground: metrics.hasBackground,
          stretchEffect: { x: 1, y: 1 },
          offset: { x: 0, y: 0 },
          squash: { x: 1, y: 1 },
          elementType: metrics.elementType,
          elementWidth: metrics.elementWidth,
          elementHeight: metrics.elementHeight,
          elementBorderRadius: metrics.elementBorderRadius
        });
        
        // Для кнопок с фоном сразу отключаем все эффекты
        if (metrics.hasBackground) {
          setCircleOffset({ x: 0, y: 0 });
          setCircleSquash({ x: 1, y: 1 });
          setStretchEffect({ x: 1, y: 1 });
          setIsPulling(false);
          offsetHistoryRef.current = []; // Очищаем историю смещений
        } else {
          // Для кнопок без фона: если курсор внутри кнопки, сбрасываем все эффекты
          if (isInside) {
            setCircleOffset({ x: 0, y: 0 });
            setCircleSquash({ x: 1, y: 1 });
            setStretchEffect({ x: 1, y: 1 });
            setIsPulling(false);
          }
        }
        
        targetX = metrics.center.x;
        targetY = metrics.center.y;
        
        setIsSticky(true);
        isDetachingRef.current = false;
        stickyElementRef.current = closestElement;
        setStickyTarget({
          x: metrics.center.x,
          y: metrics.center.y,
          width: metrics.width,
          height: metrics.height,
          borderRadius: metrics.borderRadius
        });
        
        smoothFactor = 0.2;
      } else if (isSticky && stickyElementRef.current) {
        // ПРОВЕРКА: если курсор находится над другим элементом, переключаемся на него
        let currentStickyElement = stickyElementRef.current;
        let switchedElement = false;
        
        if (elementUnderCursor && elementUnderCursor !== stickyElementRef.current) {
          // Проверяем расстояние до текущего и нового элемента
          const currentRect = stickyElementRef.current.getBoundingClientRect();
          const currentCenter = {
            x: currentRect.left + currentRect.width / 2,
            y: currentRect.top + currentRect.height / 2
          };
          
          const newRect = elementUnderCursor.getBoundingClientRect();
          const newCenter = {
            x: newRect.left + newRect.width / 2,
            y: newRect.top + newRect.height / 2
          };
          
          const distToCurrent = distance(position.x, position.y, currentCenter.x, currentCenter.y);
          const distToNew = distance(position.x, position.y, newCenter.x, newCenter.y);
          
          // Переключаемся на новый элемент только если он значительно ближе
          if (distToNew < distToCurrent * 0.8) {
            currentStickyElement = elementUnderCursor;
            stickyElementRef.current = elementUnderCursor;
            switchedElement = true;
            
            // Очищаем историю смещений при переключении элемента
            offsetHistoryRef.current = [];
          }
        }
        
        const metrics = getElementMetrics(currentStickyElement);
        const isInside = isCursorInsideStickyArea(position, metrics);
        setIsInsideStickyArea(isInside);
        isInsideButtonRef.current = isInside;
        
        // ОБНОВЛЯЕМ РАЗМЕР КРУГА ПРИ ПЕРЕКЛЮЧЕНИИ ЭЛЕМЕНТА
        if (switchedElement) {
          updateCircleSize(metrics);
        }
        
        // ДЛЯ КНОПОК БЕЗ ФОНА: рассчитываем все эффекты ТОЛЬКО когда курсор вне кнопки
        if (!metrics.hasBackground && !isInside) {
          // Рассчитываем смещение пузыря в сторону курсора
          const newOffset = calculateCircleOffset(
            position, 
            metrics.center, 
            metrics.originalRect,
            metrics.isSmallRoundButton,
            metrics.hasBackground,
            isInside
          );
          
          offsetHistoryRef.current.push(newOffset);
          if (offsetHistoryRef.current.length > OFFSET_SMOOTH_HISTORY) {
            offsetHistoryRef.current.shift();
          }
          
          const smoothOffset = offsetHistoryRef.current.reduce(
            (acc, offset) => ({ x: acc.x + offset.x, y: acc.y + offset.y }),
            { x: 0, y: 0 }
          );
          const count = offsetHistoryRef.current.length;
          
          const finalOffset = {
            x: smoothOffset.x / count,
            y: smoothOffset.y / count
          };
          
          setCircleOffset(finalOffset);
          
          // Рассчитываем сплющивание пузыря
          const squash = calculateCircleSquash(
            position,
            metrics.center,
            metrics.originalRect,
            finalOffset,
            metrics.isSmallRoundButton,
            metrics.hasBackground,
            isInside
          );
          setCircleSquash(squash);
          
          // Обновляем растягивание пузыря
          const stretch = calculateStretchEffect(
            position, 
            { 
              x: metrics.center.x + finalOffset.x, 
              y: metrics.center.y + finalOffset.y 
            }, 
            metrics.originalRect,
            metrics.isSmallRoundButton,
            metrics.hasBackground,
            isInside
          );
          setStretchEffect(stretch);
          
          // Обновляем отладочную информацию
          setDebugInfo({
            show: true,
            x: metrics.center.x,
            y: metrics.center.y,
            circleWidth: metrics.width,
            circleHeight: metrics.height,
            borderRadius: metrics.borderRadius,
            isInsideButton: isInside,
            buttonHasBackground: metrics.hasBackground,
            stretchEffect: stretch,
            offset: finalOffset,
            squash: squash,
            elementType: metrics.elementType,
            elementWidth: metrics.elementWidth,
            elementHeight: metrics.elementHeight,
            elementBorderRadius: metrics.elementBorderRadius
          });
          
          // Позиция пузыря с учетом смещения
          targetX = metrics.center.x + finalOffset.x;
          targetY = metrics.center.y + finalOffset.y;
        } else {
          // ДЛЯ КНОПОК С ФОНОМ: пузырь неподвижно привязан к центру кнопки
          // ИЛИ для кнопок без фона, когда курсор внутри кнопки
          targetX = metrics.center.x;
          targetY = metrics.center.y;
          setCircleOffset({ x: 0, y: 0 });
          setCircleSquash({ x: 1, y: 1 });
          setStretchEffect({ x: 1, y: 1 });
          setIsPulling(false);

          // Обновляем отладочную информацию
          setDebugInfo({
            show: true,
            x: metrics.center.x,
            y: metrics.center.y,
            circleWidth: metrics.width,
            circleHeight: metrics.height,
            borderRadius: metrics.borderRadius,
            isInsideButton: isInside,
            buttonHasBackground: metrics.hasBackground,
            stretchEffect: { x: 1, y: 1 },
            offset: { x: 0, y: 0 },
            squash: { x: 1, y: 1 },
            elementType: metrics.elementType,
            elementWidth: metrics.elementWidth,
            elementHeight: metrics.elementHeight,
            elementBorderRadius: metrics.elementBorderRadius
          });
        }
        
        // Проверяем расстояние для отлипания
        const dist = distance(position.x, position.y, metrics.center.x, metrics.center.y);
        
        // Динамический порог отрыва в зависимости от размера кнопки
        let stickyThresholdOut;
        if (metrics.isSmallElement) {
          stickyThresholdOut = STICKY_THRESHOLD_OUT_SMALL;
        } else {
          stickyThresholdOut = STICKY_THRESHOLD_OUT_LARGE;
        }
        
        // Дополнительная проверка для кнопок без фона (увеличиваем порог)
        if (!metrics.hasBackground) {
          stickyThresholdOut += 40;
        }
        
        if (dist > stickyThresholdOut) {
          setShowCircle(false);
          setIsSticky(false);
          setIsInsideStickyArea(false);
          isInsideButtonRef.current = false;
          isDetachingRef.current = true;
          stickStartTimeRef.current = Date.now();
          stickyElementRef.current = null;
          
          // Сбрасываем параметры пузыря
          setCircleSize({ width: 60, height: 60, borderRadius: 30 });
          lastCircleSizeRef.current = { width: 60, height: 60, borderRadius: 30 };
          setCircleOffset({ x: 0, y: 0 });
          setCircleSquash({ x: 1, y: 1 });
          offsetHistoryRef.current = [];
          setStretchEffect({ x: 1, y: 1 });
          setIsPulling(false);
          
          // Скрываем отладочную информацию
          setDebugInfo(prev => ({ ...prev, show: false }));
        }
        
        smoothFactor = 0.2;
      } else if (!isSticky && showCircle) {
        // Дополнительная проверка: если не прилипли, но пузырь еще виден
        setShowCircle(false);
        setIsInsideStickyArea(false);
        isInsideButtonRef.current = false;
        setCircleSize({ width: 60, height: 60, borderRadius: 30 });
        lastCircleSizeRef.current = { width: 60, height: 60, borderRadius: 30 };
        setCircleOffset({ x: 0, y: 0 });
        setCircleSquash({ x: 1, y: 1 });
        setStretchEffect({ x: 1, y: 1 });
        setIsPulling(false);
        
        // Скрываем отладочную информацию
        setDebugInfo(prev => ({ ...prev, show: false }));
      }

      // Плавное следование лого за курсором
      if (!isSticky) {
        // Используем физику пружины для плавного движения
        const springStrength = 0.2;
        const damping = 0.9;
        
        // Разница между текущей позицией и позицией мыши
        const dx = targetX - logoAnimationRef.current.x;
        const dy = targetY - logoAnimationRef.current.y;
        
        // Добавляем "силу" пружины
        logoVelocityRef.current.x += dx * springStrength;
        logoVelocityRef.current.y += dy * springStrength;
        
        // Демпфирование для плавного останова
        logoVelocityRef.current.x *= damping;
        logoVelocityRef.current.y *= damping;
        
        // Обновляем позицию
        logoAnimationRef.current.x += logoVelocityRef.current.x;
        logoAnimationRef.current.y += logoVelocityRef.current.y;
        
        // Используем позицию из физики пружины
        targetX = logoAnimationRef.current.x;
        targetY = logoAnimationRef.current.y;
        
        smoothFactor = 0.15;
      } else {
        // Когда прилипли, сбрасываем физику пружины
        logoAnimationRef.current.x = targetX;
        logoAnimationRef.current.y = targetY;
        logoVelocityRef.current.x = 0;
        logoVelocityRef.current.y = 0;
      }

      // Плавный переход к целевой позиции
      const offsetX = 0;
      const offsetY = 0;
      const targetSvgX = targetX + offsetX;
      const targetSvgY = targetY + offsetY;
      
      const speedFactor = Math.min(speed / 20, 1);
      const dynamicSmoothFactor = lerp(smoothFactor, 0.25, speedFactor);
      
      const newSvgX = lerp(svgPosition.x, targetSvgX, dynamicSmoothFactor);
      const newSvgY = lerp(svgPosition.y, targetSvgY, dynamicSmoothFactor);
      
      setSvgPosition({ x: newSvgX, y: newSvgY });
      
      rafRef.current = requestAnimationFrame(animate);
    };

    // Инициализация начальной позиции
    logoAnimationRef.current.x = position.x;
    logoAnimationRef.current.y = position.y;
    lastCircleSizeRef.current = { width: 60, height: 60, borderRadius: 30 };
    
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [position, svgPosition, velocity, isSticky, scale, rotation, showCircle, circleOffset, isPopupOpen]);

  const supportsBackdropFilter = () => {
    return CSS.supports('backdrop-filter', 'invert(1)') || 
           CSS.supports('-webkit-backdrop-filter', 'invert(1)');
  };

  const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');

  // ЕСЛИ ПОПАП ОТКРЫТ ИЛИ БРАУЗЕР НЕ ПОДДЕРЖИВАЕТ - НЕ РЕНДЕРИМ КУРСОР
  if (isPopupOpen || !supportsBackdropFilter() || isFirefox) {
    return null;
  }

  // Для кнопок с фоном или когда курсор внутри кнопки без фона - простая трансформация без смещений
  // Для кнопок без фона и когда курсор снаружи - полная трансформация со всеми эффектами
  const shouldShowEffects = !buttonHasBackground && !isInsideStickyArea;
  const combinedTransform = shouldShowEffects
    ? `translate(${circleOffset.x}px, ${circleOffset.y}px)
       scale(${circleSquash.x}, ${circleSquash.y})
       scale(${stretchEffect.x}, ${stretchEffect.y})`
    : 'translate(0px, 0px) scale(1, 1) scale(1, 1)';

  const logoOpacity = showCircle ? 0 : 1;
  const logoDisplay = showCircle ? 'none' : 'block';

  return (
    <div className="cursor-container">
      {/* Лого-курсор (скрывается при прилипании) */}
      <div 
        className={`cursor-lens-backdrop ${isClicked ? 'clicked' : ''} ${isHovering ? 'hover' : ''}`}
        style={{
          left: `${svgPosition.x}px`,
          top: `${svgPosition.y}px`,
          transform: `translate(-50%, -50%) 
                     rotate(${rotation}rad)
                     scale(${scale.x}, ${scale.y})`,
          opacity: logoOpacity,
          display: logoDisplay,
          transition: 'opacity 0.1s ease-out, display 0.1s step-end'
        }}
      />
      
      {/* Пузырь для прилипания (показывается только при прилипании) */}
      {showCircle && (
        <div 
          className={`cursor-sticky-circle ${isClicked ? 'clicked' : ''} ${buttonHasBackground ? 'inside-button' : 'outside-button'} ${isPulling ? 'pulling' : ''}`}
          style={{
            left: `${svgPosition.x}px`,
            top: `${svgPosition.y}px`,
            width: `${circleSize.width}px`,
            height: `${circleSize.height}px`,
            borderRadius: `${circleSize.borderRadius}px`,
            transform: `translate(-50%, -50%) ${combinedTransform}`,
            opacity: 1,
            transformOrigin: 'center',
            transition: !shouldShowEffects 
              ? 'opacity 0.1s ease-out, width 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), height 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), border-radius 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)' 
              : 'opacity 0.1s ease-out, transform 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94), width 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), height 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), border-radius 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        />
      )}

      
    </div>
  );
};

export default GlobalCursor;